/**
 * Monkey Wheel — game bootstrap + render + backend wiring.
 *
 * The visual layer is an exact DOM/CSS port of the approved prototype
 * (monkey-wheel/Monkey Wheel.dc.html). The local simulation in that prototype
 * (bot bets, weightedPick, fake balance, local payouts) is replaced here by
 * server-authoritative state delivered over Socket.IO + REST, reusing the same
 * clock-reconciliation / bet-batch infrastructure as the Ferris game.
 */

import { config, isInFlutter, haptic, requestBalanceRefresh, closeGame } from './bridge.js';
import { api } from './api.js';
import { onSocketLifecycle, connectSocket, disconnectSocket, on, emit } from './socket.js';
import { mergeMyBets, isCurrentRound, classifyBetFailure, summarizeMyResult } from './result.js';
import {
  getState, setState, subscribe,
  applyServerNow, applyServerRemaining, authoritativeRemainingMs,
} from './store.js';
import {
  unlockAudio, setMuted as setAudioMuted, isMuted as isAudioMuted, sfx,
} from './sound.js';

// ---- Layout constants (verbatim from prototype) ----
const STAGE_W = 414;
// Height of the scaled "wheel cluster" sub-box (everything above the pinned bet
// dock). The original prototype was 414×896 and reserved 672-896 for the chip
// panel; that panel is now a separate pinned dock, so the wheel box is cropped
// to that 672 boundary.
const WHEEL_H = 672;
// Screen positions by slot.position index (0-9). 0-7 ring the wheel; 8-9 are
// the bottom-corner bonus baskets.
const POS = [
  [207, 180], [313, 224], [357, 330], [313, 436], [207, 480],
  [101, 436], [57, 330], [101, 224], [60, 602], [354, 602],
];
// Spin traversal order across positions (includes bonus baskets).
const LOOP = [0, 1, 2, 3, 9, 4, 8, 5, 6, 7];
const LIGHT_CX = 207, LIGHT_CY = 330, LIGHT_R = 176, LIGHT_N = 28;
const CHIP_COLORS = ['#34c759', '#1fc8d6', '#d6298f', '#2f6df0', '#ff8c1a', '#9b59b6'];

// ---- Bet-batch queue tuning (ported from Ferris) ----
const BET_BATCH_DELAY_MS = 250;
const BET_BATCH_FINAL_FLUSH_MS = 1200;
const BET_BATCH_MAX_TAPS = 50;

// ---- Local (non-store) UI state ----
const ui = {
  loading: true,
  loadProgress: 0,
  frame: 0,
  spinPos: -1,        // highlighted position during spin (-1 = none)
  selectedChip: 0,
  auto: false,
  muted: false,
  result: null,       // { win, amount, emoji, mult }
  lastBets: {},       // snapshot of myBets at spin start, for auto-bet replay
  myUserId: null,
};

let betQueue = [];
let betFlushTimer = null;
let betFlushInFlight = false;
let betBatchSeq = 0;
let spinTimer = null;
let resultHideTimer = null;
let loadTimer = null;
let frameTimer = null;
let pingTimer = null;
let lastRttMs = null;
let destroyed = false;

const dom = {}; // persistent element refs

// ---------- formatting helpers ----------
function fmtK(n) {
  n = Math.round(Number(n) || 0);
  if (n >= 1000) { const v = n / 1000; return (v >= 100 ? Math.round(v) : Math.round(v * 10) / 10) + 'K'; }
  return '' + n;
}
function fmtComma(n) { return (Math.round(Number(n) || 0)).toLocaleString('en-US'); }
function makeNonce() { return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`; }

function slots() { return (getState().config && getState().config.slots) || []; }
function slotByPosition(pos) { return slots().find((s) => Number(s.position) === Number(pos)) || null; }
function slotById(id) { return slots().find((s) => Number(s.id) === Number(id)) || null; }

// ===================================================================
// DOM construction (persistent nodes; updated in place to keep CSS
// transitions/animations intact — never re-built every frame).
// ===================================================================
function buildStage() {
  const app = document.getElementById('mw-app');
  app.innerHTML = `
  <div id="mw-stage">

    <!-- TOP CHROME (pinned, always tappable regardless of sheet height) -->
    <div id="mw-top">
      <div data-action="close" style="width:30px;height:30px;flex:none;border-radius:50%;background:linear-gradient(#ff5a6e,#d62b46);border:2px solid #ffd0d8;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;font-weight:800;box-shadow:0 2px 4px rgba(0,0,0,.4);cursor:pointer;">‹</div>
      <div class="mw-recent-pill" style="flex:1;min-width:0;height:30px;border-radius:15px;background:rgba(20,40,18,.7);border:1.5px solid rgba(255,210,90,.5);display:flex;align-items:center;padding:0 6px;gap:3px;overflow:hidden;">
        <span style="flex:none;background:#e23;color:#fff;font-size:8px;font-weight:800;padding:1px 4px;border-radius:5px;">NEW</span>
        <span id="mw-recent" style="display:flex;align-items:center;gap:3px;overflow:hidden;"></span>
        <span style="flex:none;color:#ffd23f;font-size:12px;">▸</span>
      </div>
      <div style="flex:none;height:28px;border-radius:14px;background:linear-gradient(#3f7d35,#2a5a23);border:1.5px solid rgba(255,210,90,.5);display:flex;align-items:center;gap:4px;padding:0 9px;color:#fff;font-size:11px;font-weight:600;">win today <span style="font-size:12px;">🪙</span> <b id="mw-wintoday" style="color:#ffe14d;">0</b></div>
      <div data-action="menu" style="width:28px;height:28px;flex:none;border-radius:8px;background:linear-gradient(#ff5a6e,#d62b46);display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;box-shadow:0 2px 4px rgba(0,0,0,.4);cursor:pointer;">≡</div>
    </div>

    <!-- WHEEL ZONE: flex:1 region; the wheel cluster is scaled to fit it. -->
    <div id="mw-mid">
      <div id="mw-wheelbox" style="width:${STAGE_W}px;height:${WHEEL_H}px;">
      <div style="position:absolute;inset:0;overflow:hidden;border-radius:2px;background:radial-gradient(ellipse 120% 80% at 50% 22%,#4ba552 0%,#2f8038 30%,#1d5429 60%,#0e3a1c 84%,#072512 100%);font-family:'Baloo 2',system-ui,sans-serif;">

        <!-- jungle depth: layered canopy + ground tints -->
        <div style="position:absolute;inset:0;background:linear-gradient(180deg,rgba(8,40,18,.55) 0%,transparent 24%,transparent 70%,rgba(6,26,12,.6) 100%),radial-gradient(circle at 18% 14%,rgba(120,200,110,.25),transparent 38%),radial-gradient(circle at 84% 20%,rgba(90,170,90,.22),transparent 40%);pointer-events:none;"></div>
        <!-- blurred foliage silhouettes for depth -->
        <div style="position:absolute;top:8px;left:-14px;font-size:120px;opacity:.14;filter:blur(2px);transform:rotate(-18deg);">🌴</div>
        <div style="position:absolute;top:-10px;right:-18px;font-size:120px;opacity:.13;filter:blur(2px);transform:rotate(20deg) scaleX(-1);">🌴</div>
        <div style="position:absolute;top:360px;right:-16px;font-size:90px;opacity:.12;filter:blur(1.5px);">🌿</div>
        <div style="position:absolute;bottom:150px;left:-18px;font-size:96px;opacity:.12;filter:blur(1.5px);transform:scaleX(-1);">🌿</div>
        <div style="position:absolute;top:30px;left:24px;font-size:34px;opacity:.2;">🍃</div>
        <div style="position:absolute;inset:0;background:radial-gradient(circle at 50% 40%,transparent 52%,rgba(0,0,0,.5));pointer-events:none;"></div>

        <div style="position:absolute;top:48px;left:48px;background:rgba(0,0,0,.32);border-radius:6px;padding:2px 9px;color:#fff;font-size:12px;font-weight:600;z-index:18;">Round: <span id="mw-round">—</span></div>
        <div id="mw-ping" style="position:absolute;top:48px;right:48px;color:#bff5c4;font-size:11px;font-weight:600;z-index:18;display:flex;align-items:center;gap:3px;">📶 —</div>

        <!-- side icons -->
        <div style="position:absolute;top:78px;left:6px;z-index:18;display:flex;flex-direction:column;align-items:center;gap:14px;">
          <div data-action="profile" style="width:38px;height:38px;border-radius:50%;background:linear-gradient(#ffb43f,#e07c12);border:2px solid #fff3cf;display:flex;align-items:center;justify-content:center;font-size:19px;box-shadow:0 2px 5px rgba(0,0,0,.4);cursor:pointer;">👤</div>
          <div data-action="moneyjar" style="display:flex;flex-direction:column;align-items:center;cursor:pointer;">
            <div style="width:42px;height:42px;border-radius:10px;background:linear-gradient(#ffd86b,#e89a14);border:2px solid #fff3cf;display:flex;align-items:center;justify-content:center;font-size:22px;box-shadow:0 2px 6px rgba(0,0,0,.45);">🐷</div>
            <span style="color:#ffe9a8;font-size:9px;font-weight:700;text-shadow:0 1px 2px #000;margin-top:1px;">Money Jar</span>
          </div>
        </div>
        <div style="position:absolute;top:78px;right:6px;z-index:18;display:flex;flex-direction:column;align-items:center;gap:14px;">
          <div data-action="help" style="width:38px;height:38px;border-radius:50%;background:linear-gradient(#ffb43f,#e07c12);border:2px solid #fff3cf;display:flex;align-items:center;justify-content:center;font-size:19px;font-weight:800;color:#fff;box-shadow:0 2px 5px rgba(0,0,0,.4);cursor:pointer;">?</div>
          <div id="mw-sound" data-action="mute" style="width:38px;height:38px;border-radius:50%;background:linear-gradient(#ffb43f,#e07c12);border:2px solid #fff3cf;display:flex;align-items:center;justify-content:center;font-size:18px;box-shadow:0 2px 5px rgba(0,0,0,.4);cursor:pointer;">🔊</div>
        </div>

        <!-- WHEEL -->
        <div style="position:absolute;inset:0;z-index:6;">
          <!-- ornate gold ferris frame ring -->
          <div style="position:absolute;left:207px;top:330px;width:360px;height:360px;transform:translate(-50%,-50%);border-radius:50%;background:conic-gradient(#ffe89a,#d98a00,#ffefb0,#c8780a,#ffe89a,#d98a00,#ffefb0,#c8780a,#ffe89a);box-shadow:0 7px 22px rgba(0,0,0,.55),inset 0 0 0 2px rgba(120,60,0,.45);"></div>
          <!-- slow rotating sheen over the frame -->
          <div style="position:absolute;left:207px;top:330px;width:360px;height:360px;transform:translate(-50%,-50%);border-radius:50%;background:conic-gradient(from 0deg,transparent 0deg,rgba(255,255,255,.55) 22deg,transparent 56deg,transparent 360deg);-webkit-mask:radial-gradient(circle,transparent 76%,#000 78%);mask:radial-gradient(circle,transparent 76%,#000 78%);animation:mw-frame-shimmer 6s linear infinite;pointer-events:none;z-index:7;"></div>
          <!-- red inner disc -->
          <div style="position:absolute;left:207px;top:330px;width:320px;height:320px;transform:translate(-50%,-50%);border-radius:50%;background:radial-gradient(circle at 50% 38%,#dc4029 0%,#c0331f 52%,#911f12 100%);box-shadow:inset 0 0 28px rgba(0,0,0,.6),inset 0 0 0 3px #ffce4a;"></div>
          <!-- spokes from hub to ring baskets (built from POS) -->
          <div id="mw-spokes"></div>
          <div id="mw-lights"></div>
          <div id="mw-baskets"></div>
          <!-- center hub halo -->
          <div style="position:absolute;left:207px;top:330px;width:188px;height:188px;transform:translate(-50%,-50%);border-radius:50%;background:radial-gradient(circle,rgba(255,231,140,.55),rgba(255,210,70,0) 70%);animation:mw-halo 2.4s ease-in-out infinite;pointer-events:none;z-index:8;"></div>
          <div style="position:absolute;left:207px;top:330px;width:150px;height:150px;transform:translate(-50%,-50%);border-radius:50%;background:radial-gradient(circle at 50% 38%,#fff3b0,#ffd23f 60%,#f0a000);border:5px solid #fff;box-shadow:0 0 0 3px #d98a00,0 0 0 6px rgba(255,206,74,.7),0 6px 16px rgba(0,0,0,.55);display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:9;">
            <div style="font-size:40px;line-height:.8;margin-top:-4px;filter:drop-shadow(0 2px 2px rgba(0,0,0,.3));">🐵</div>
            <div style="font-family:'Luckiest Guy',cursive;font-size:21px;line-height:1;color:#8a3df0;text-shadow:0 1px 0 #fff,0 2px 2px rgba(0,0,0,.25);">Greedy</div>
            <div id="mw-centerbig" style="font-family:'Luckiest Guy',cursive;font-size:26px;line-height:1;color:#19b6d6;text-shadow:0 1px 0 #fff,0 2px 3px rgba(0,0,0,.3);">★</div>
          </div>
        </div>

        <!-- INFO ROW -->
        <div style="position:absolute;top:592px;left:0;right:0;display:flex;justify-content:center;gap:36px;z-index:7;">
          <div style="text-align:center;color:#fff;text-shadow:0 1px 2px #000;">
            <div style="font-size:11px;opacity:.85;">Total Bet</div>
            <div id="mw-totalbet" style="font-family:'Baloo 2';font-weight:800;font-size:18px;color:#ffe14d;">0</div>
          </div>
          <div style="text-align:center;color:#fff;text-shadow:0 1px 2px #000;">
            <div style="font-size:11px;opacity:.85;">My total bet</div>
            <div id="mw-mytotalbet" style="font-family:'Baloo 2';font-weight:800;font-size:18px;color:#9bff7a;">0</div>
          </div>
        </div>

        <div style="position:absolute;top:650px;left:0;right:0;text-align:center;color:#ffe9a8;font-size:11px;font-weight:600;z-index:7;text-shadow:0 1px 2px #000;">Operation Steps: Select Amount &gt; Click Bet</div>

        <!-- (bet dock moved out of the scaled wheel box; see #mw-dock below) -->

        <!-- RESULT POPUP -->
        <div id="mw-result" style="display:none;position:absolute;left:207px;top:300px;transform:translate(-50%,-50%);z-index:30;width:248px;padding:18px 16px;border-radius:18px;background:linear-gradient(#fff7e0,#ffe3a6);border:4px solid #ffae00;box-shadow:0 10px 30px rgba(0,0,0,.5);text-align:center;">
          <div id="mw-result-emoji" style="font-size:46px;line-height:1;"></div>
          <div id="mw-result-title" style="font-family:'Luckiest Guy',cursive;font-size:26px;margin-top:4px;"></div>
          <div id="mw-result-sub" style="font-family:'Baloo 2';font-weight:700;font-size:15px;color:#7a3b00;margin-top:2px;"></div>
          <div id="mw-result-bet" style="font-family:'Baloo 2';font-weight:700;font-size:13px;color:#7a3b00;margin-top:6px;"></div>
          <div id="mw-result-net" style="font-family:'Luckiest Guy',cursive;font-size:20px;margin-top:1px;"></div>
          <div id="mw-result-winners" style="margin-top:10px;text-align:left;"></div>
        </div>

      </div><!-- /jungle art -->
      </div><!-- /#mw-wheelbox -->
    </div><!-- /#mw-mid -->

    <!-- BET DOCK (pinned below the wheel; stays readable at any sheet height) -->
    <div id="mw-dock">
      <div class="mw-dock-row1">
        <div id="mw-coins-box">
          <div class="mw-coins-label">Have Game Coins</div>
          <div class="mw-coins-val">🪙 <span id="mw-coins">—</span></div>
        </div>
        <div id="mw-chips"></div>
        <div id="mw-auto" data-action="auto">auto bet</div>
      </div>
    </div>

        <!-- MODAL (profile / money jar / help / menu panels) -->
        <div id="mw-modal" data-action="close-modal" style="display:none;position:absolute;inset:0;z-index:40;background:rgba(0,0,0,.6);align-items:center;justify-content:center;padding:18px;">
          <div id="mw-modal-card" data-action="modal-card" style="width:100%;max-width:300px;max-height:78%;display:flex;flex-direction:column;border-radius:18px;background:linear-gradient(#fff7e0,#ffe3a6);border:4px solid #ffae00;box-shadow:0 12px 34px rgba(0,0,0,.55);overflow:hidden;">
            <div style="flex:none;display:flex;align-items:center;gap:8px;padding:12px 14px;background:linear-gradient(#7a1f12,#5c1610);border-bottom:3px solid #ffce4a;">
              <span id="mw-modal-icon" style="font-size:22px;line-height:1;"></span>
              <span id="mw-modal-title" style="flex:1;font-family:'Luckiest Guy',cursive;font-size:20px;color:#ffe14d;text-shadow:0 1px 2px rgba(0,0,0,.5);"></span>
              <div data-action="close-modal" style="width:28px;height:28px;flex:none;border-radius:50%;background:linear-gradient(#ff5a6e,#d62b46);border:2px solid #ffd0d8;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;font-weight:800;cursor:pointer;">×</div>
            </div>
            <div id="mw-modal-body" style="flex:1;min-height:0;overflow-y:auto;padding:14px;font-family:'Baloo 2',system-ui,sans-serif;color:#5e2a00;font-size:13px;"></div>
          </div>
        </div>

        <!-- LOADING -->
        <div id="mw-loading" data-action="skip-load" style="position:absolute;inset:0;z-index:50;cursor:pointer;background:radial-gradient(circle at 50% 35%,#27160a,#0c0703);display:flex;flex-direction:column;align-items:center;justify-content:center;">
          <div style="font-size:96px;animation:mw-bounce 1.1s ease-in-out infinite;filter:drop-shadow(0 8px 10px rgba(0,0,0,.5));">🐵</div>
          <div style="font-family:'Luckiest Guy',cursive;font-size:42px;line-height:.95;text-align:center;margin-top:8px;background:linear-gradient(90deg,#ffd23f,#7be04a,#ffd23f);background-size:200% auto;-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-stroke:2px #5e2a00;animation:mw-shine 2.2s linear infinite;">MONKEY<br>WHEEL!</div>
          <div style="color:#ffe9a8;font-size:12px;letter-spacing:3px;margin-top:6px;opacity:.7;">QUANTUM NEXUS</div>
          <div style="width:260px;height:20px;border-radius:11px;background:rgba(0,0,0,.4);border:2px solid #ffce4a;margin-top:40px;overflow:hidden;padding:2px;">
            <div id="mw-loadbar" style="height:100%;width:0%;border-radius:8px;background:linear-gradient(90deg,#ffd23f,#ff9d00);transition:width .12s linear;"></div>
          </div>
          <div style="color:#fff;font-size:12px;font-weight:600;margin-top:8px;">Loading <span id="mw-loadpct">0</span>%</div>
          <div style="color:#ffe9a8;font-size:11px;font-weight:600;margin-top:18px;opacity:.6;">tap to enter</div>
        </div>

  </div>`;

  dom.stage = document.getElementById('mw-stage');
  dom.mid = document.getElementById('mw-mid');
  dom.wheelbox = document.getElementById('mw-wheelbox');
  dom.recent = document.getElementById('mw-recent');
  dom.winToday = document.getElementById('mw-wintoday');
  dom.round = document.getElementById('mw-round');
  dom.ping = document.getElementById('mw-ping');
  dom.sound = document.getElementById('mw-sound');
  dom.spokes = document.getElementById('mw-spokes');
  dom.lights = document.getElementById('mw-lights');
  dom.baskets = document.getElementById('mw-baskets');
  dom.centerBig = document.getElementById('mw-centerbig');
  dom.totalBet = document.getElementById('mw-totalbet');
  dom.myTotalBet = document.getElementById('mw-mytotalbet');
  dom.coins = document.getElementById('mw-coins');
  dom.chips = document.getElementById('mw-chips');
  dom.auto = document.getElementById('mw-auto');
  dom.result = document.getElementById('mw-result');
  dom.resultEmoji = document.getElementById('mw-result-emoji');
  dom.resultTitle = document.getElementById('mw-result-title');
  dom.resultSub = document.getElementById('mw-result-sub');
  dom.resultBet = document.getElementById('mw-result-bet');
  dom.resultNet = document.getElementById('mw-result-net');
  dom.resultWinners = document.getElementById('mw-result-winners');
  dom.loading = document.getElementById('mw-loading');
  dom.loadBar = document.getElementById('mw-loadbar');
  dom.loadPct = document.getElementById('mw-loadpct');
  dom.modal = document.getElementById('mw-modal');
  dom.modalCard = document.getElementById('mw-modal-card');
  dom.modalIcon = document.getElementById('mw-modal-icon');
  dom.modalTitle = document.getElementById('mw-modal-title');
  dom.modalBody = document.getElementById('mw-modal-body');

  buildSpokes();
  buildLights();
  observeLayout();
  layout();
}

// Gold spokes radiating from the hub (LIGHT_CX/CY) to each of the 8 ring
// baskets (positions 0-7). Built once from POS so they always align with the
// baskets even if POS changes. Bonus baskets (8-9) are off-wheel — no spokes.
function buildSpokes() {
  if (!dom.spokes) return;
  const frag = document.createDocumentFragment();
  for (let pos = 0; pos < 8; pos++) {
    const [px, py] = POS[pos];
    const dx = px - LIGHT_CX, dy = py - LIGHT_CY;
    const len = Math.sqrt(dx * dx + dy * dy);
    const deg = Math.atan2(dy, dx) * 180 / Math.PI;
    const el = document.createElement('div');
    el.style.cssText = `position:absolute;left:${LIGHT_CX}px;top:${LIGHT_CY}px;width:${len}px;height:9px;transform-origin:0 50%;transform:translate(0,-50%) rotate(${deg}deg);background:linear-gradient(90deg,#b5400f 0%,#ffce4a 30%,#fff3b0 50%,#ffce4a 70%,#b5400f 100%);border-radius:5px;box-shadow:0 1px 2px rgba(0,0,0,.45),inset 0 1px 0 rgba(255,255,255,.4);`;
    frag.appendChild(el);
  }
  dom.spokes.appendChild(frag);
}

function buildLights() {
  dom.lightNodes = [];
  const frag = document.createDocumentFragment();
  for (let i = 0; i < LIGHT_N; i++) {
    const a = (i / LIGHT_N) * Math.PI * 2;
    const x = LIGHT_CX + LIGHT_R * Math.sin(a);
    const y = LIGHT_CY - LIGHT_R * Math.cos(a);
    // Dark socket ring (static) so lit/unlit bulbs read clearly on the frame.
    const socket = document.createElement('div');
    socket.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:14px;height:14px;transform:translate(-50%,-50%);border-radius:50%;background:radial-gradient(circle,#5c1610,#2a0a06);box-shadow:inset 0 0 2px rgba(0,0,0,.8);`;
    frag.appendChild(socket);
    // Bulb (animated by updateLights via background + boxShadow).
    const el = document.createElement('div');
    el.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:10px;height:10px;transform:translate(-50%,-50%);border-radius:50%;background:#b5400f;`;
    frag.appendChild(el);
    dom.lightNodes.push(el);
  }
  dom.lights.appendChild(frag);
}

// Baskets depend on config slots; build once config is loaded.
function buildBaskets() {
  if (dom.basketNodes) return;
  dom.baskets.innerHTML = '';
  dom.basketNodes = [];
  for (let pos = 0; pos < POS.length; pos++) {
    const slot = slotByPosition(pos);
    const [x, y] = POS[pos];
    const bettable = slot ? slot.bettable !== false : false;
    const wrap = document.createElement('div');
    // Non-bettable slots stay on the wheel (and can be highlighted as the
    // winner-less spin target) but are not tappable for bets.
    if (bettable) wrap.dataset.action = 'bet';
    wrap.dataset.slotId = slot ? slot.id : '';
    wrap.dataset.pos = String(pos);
    wrap.innerHTML = `
      <!-- striped gold/red canopy with finial (replaces the crown emoji) -->
      <div style="position:relative;width:60px;height:14px;margin-bottom:-4px;border-radius:9px 9px 4px 4px;background:repeating-linear-gradient(90deg,#e2452f 0 8px,#ffcf4d 8px 16px);border:1.5px solid #ffe89a;box-shadow:0 2px 3px rgba(0,0,0,.4),inset 0 1px 0 rgba(255,255,255,.45);z-index:2;">
        <div style="position:absolute;left:50%;top:-6px;transform:translateX(-50%);width:5px;height:8px;background:linear-gradient(#ffe89a,#d98a00);border-radius:50% 50% 2px 2px;"></div>
      </div>
      <!-- woven gondola basket -->
      <div style="position:relative;width:84px;padding:7px 5px 6px;border-radius:10px 10px 18px 18px;background:repeating-linear-gradient(45deg,rgba(0,0,0,.10) 0 4px,transparent 4px 8px),repeating-linear-gradient(-45deg,rgba(255,255,255,.07) 0 4px,transparent 4px 8px),linear-gradient(#e2452f,#b5231a 70%,#911f12);border:2px solid #ffcf4d;box-shadow:inset 0 2px 0 rgba(255,255,255,.25),inset 0 -3px 6px rgba(0,0,0,.35),0 3px 7px rgba(0,0,0,.45);display:flex;flex-direction:column;align-items:center;gap:2px;">
        <div style="position:absolute;top:2px;left:6px;right:6px;height:4px;border-radius:3px;background:linear-gradient(rgba(255,255,255,.5),rgba(255,255,255,0));pointer-events:none;"></div>
        <div style="display:flex;align-items:center;gap:3px;">
          <span class="mw-b-emoji" style="font-size:25px;line-height:1;filter:drop-shadow(0 1px 1px rgba(0,0,0,.4));"></span>
          <span class="mw-b-mult" style="font-family:'Luckiest Guy',cursive;font-size:18px;color:#ffe14d;text-shadow:-1px -1px 0 #7a1500,1px -1px 0 #7a1500,-1px 1px 0 #7a1500,1px 1px 0 #7a1500,0 2px 0 #5e1000;"></span>
        </div>
        <div style="background:linear-gradient(#fff0b0,#ffd23f 45%,#f0a500);border:1px solid #c8780a;border-radius:8px;padding:0 8px;font-weight:800;font-size:10.5px;color:#6a2b00;white-space:nowrap;box-shadow:inset 0 1px 0 rgba(255,255,255,.6),0 1px 2px rgba(0,0,0,.3);">Total <span class="mw-b-total">0</span></div>
        <div class="mw-b-stars" style="font-size:8px;letter-spacing:1px;line-height:1;"></div>
      </div>
      <div class="mw-b-mybet" style="display:none;margin-top:2px;background:#1f9d4d;border:1px solid #b9ffce;border-radius:8px;padding:0 7px;font-weight:700;font-size:10px;color:#fff;white-space:nowrap;"></div>
      <div class="mw-b-label" style="display:none;margin-top:1px;font-weight:700;font-size:11px;color:#fff;text-shadow:0 1px 2px #000;"></div>`;
    dom.baskets.appendChild(wrap);
    const node = {
      wrap,
      emoji: wrap.querySelector('.mw-b-emoji'),
      mult: wrap.querySelector('.mw-b-mult'),
      total: wrap.querySelector('.mw-b-total'),
      stars: wrap.querySelector('.mw-b-stars'),
      myBet: wrap.querySelector('.mw-b-mybet'),
      label: wrap.querySelector('.mw-b-label'),
      x, y,
    };
    if (slot) {
      node.emoji.textContent = slot.emoji || '';
      node.mult.textContent = 'x' + slot.multiplier;
      if (slot.label) { node.label.textContent = slot.label; node.label.style.display = ''; }
      if (!bettable) {
        // Dim the basket and mark it locked so players see betting is disabled.
        wrap.style.opacity = '.55';
        wrap.style.cursor = 'default';
        node.total.textContent = '🔒';
      }
    }
    node.bettable = bettable;
    dom.basketNodes.push(node);
  }
}

function buildChips() {
  const amounts = (getState().config && getState().config.quickBetAmounts) || [];
  dom.chips.innerHTML = '';
  dom.chipNodes = [];
  amounts.forEach((amount, i) => {
    const el = document.createElement('div');
    el.className = 'mw-chip';
    el.dataset.action = 'chip';
    el.dataset.amount = String(amount);
    el.style.setProperty('--mw-chip-color', CHIP_COLORS[i % CHIP_COLORS.length]);
    el.textContent = fmtK(amount);
    dom.chips.appendChild(el);
    dom.chipNodes.push({ el, amount });
  });
  if (!ui.selectedChip && amounts.length) ui.selectedChip = amounts[0];
  updateChips();
}

// ---------- responsive layout ----------
// The wheel cluster keeps the prototype's fixed STAGE_W×WHEEL_H pixel art; we
// "contain"-scale it into the flex middle zone (the space left between the
// pinned top chrome and the pinned bet dock). This guarantees the whole wheel
// AND the bet controls stay visible at any sheet height — from the 50% default
// up to near-full — with no scrolling and no overlap.
function layout() {
  const box = dom.wheelbox;
  const mid = dom.mid;
  if (!box || !mid) return;
  // Use window.innerWidth directly — mirrors how Phaser's Scale.RESIZE works
  // and avoids Android WebView discrepancies where clientWidth can lag behind
  // the actual visual width of the WebView widget.
  const availW = window.innerWidth;
  const availH = mid.clientHeight;
  if (availW <= 0 || availH <= 0) return;
  const s = Math.min(availW / STAGE_W, availH / WHEEL_H);
  // Centre vertically in whatever slack remains (contain → never clipped).
  const top = Math.max(0, (availH - WHEEL_H * s) / 2);
  box.style.transform = `translateX(-50%) scale(${s})`;
  box.style.top = top + 'px';

  // Height tiers let CSS compact the pinned chrome on very short sheets.
  // Thresholds tuned for the 60% bottom-sheet starting height.
  if (dom.stage) {
    const h = window.innerHeight;
    dom.stage.classList.toggle('mw-short', h < 500);
    dom.stage.classList.toggle('mw-tiny', h < 400);
  }
}

let layoutRaf = 0;
function scheduleLayout() {
  if (layoutRaf) return;
  layoutRaf = requestAnimationFrame(() => { layoutRaf = 0; layout(); });
}

// React to the WebView box resizing (the Flutter sheet being dragged fires
// window.resize; ResizeObserver on the root is the more reliable signal on
// Android hybrid-composition WebViews). rAF-debounced + dedup'd via scheduleLayout.
let layoutObserver = null;
function observeLayout() {
  window.addEventListener('resize', scheduleLayout);
  window.addEventListener('orientationchange', scheduleLayout);
  if (typeof ResizeObserver !== 'undefined' && !layoutObserver) {
    layoutObserver = new ResizeObserver(scheduleLayout);
    layoutObserver.observe(document.documentElement);
  }
}

// ===================================================================
// Render updaters (mutate persistent nodes; called on state change +
// from the animation frame loop for lights / countdown / spin glow).
// ===================================================================
function updateChips() {
  if (!dom.chipNodes) return;
  dom.chipNodes.forEach(({ el, amount }) => {
    el.classList.toggle('mw-chip-sel', ui.selectedChip === amount);
  });
}

function updateAuto() {
  dom.auto.classList.toggle('mw-auto-on', ui.auto);
}

function updateRecent() {
  const recent = getState().recent || [];
  dom.recent.innerHTML = recent.slice(0, 8).map((e) =>
    `<span style="flex:none;width:21px;height:21px;border-radius:50%;background:rgba(255,255,255,.12);display:flex;align-items:center;justify-content:center;font-size:13px;">${e}</span>`
  ).join('');
}

function updateBaskets() {
  if (!dom.basketNodes) return;
  const st = getState();
  const phase = st.status;
  const cfg = st.config || {};
  const maxSpots = cfg.maxSpotsPerUser || 6;
  const limitReached = phase === 'betting' && Object.keys(st.myBets).length >= maxSpots;
  for (let pos = 0; pos < dom.basketNodes.length; pos++) {
    const node = dom.basketNodes[pos];
    const slot = slotByPosition(pos);
    if (!slot) continue;
    const bettable = node.bettable !== false;
    let myBet = 0;
    if (bettable) {
      const total = Number(st.betsBySlot[String(slot.id)] || 0);
      myBet = Number(st.myBets[String(slot.id)] || 0);
      node.total.textContent = fmtK(total);
      const starLabel = total > 4000 ? '⭐⭐' : total > 1200 ? '⭐' : '';
      node.stars.textContent = starLabel;
      node.stars.style.display = starLabel ? '' : 'none';
      if (myBet > 0) { node.myBet.textContent = '+' + fmtK(myBet); node.myBet.style.display = ''; }
      else { node.myBet.style.display = 'none'; }
    } else {
      // Non-bettable: no totals/personal bets, show a lock instead.
      node.total.textContent = '🔒';
      node.stars.style.display = 'none';
      node.myBet.style.display = 'none';
    }

    const isWinner = phase === 'result' && Number(st.winningSlotId) === Number(slot.id);
    const isActive = phase === 'spinning' && ui.spinPos === pos;
    const hot = isWinner || isActive;
    let filter = '';
    if (isWinner) filter = 'drop-shadow(0 0 10px #ffe14d) drop-shadow(0 0 18px #ff9d00)';
    else if (isActive) filter = 'drop-shadow(0 0 8px #fff7c0)';
    // Lock un-bet slots once the per-round distinct-slot limit is reached.
    const locked = bettable && limitReached && myBet <= 0;
    if (bettable) node.wrap.dataset.action = locked ? '' : 'bet';
    let dim = (phase === 'result' && !isWinner) ? 'opacity:.45;' : '';
    if (!bettable && !dim) dim = 'opacity:.55;';
    const cursor = (bettable && !locked) ? 'pointer' : 'default';
    node.wrap.style.cssText = `position:absolute;left:${node.x}px;top:${node.y}px;transform:translate(-50%,-50%)${hot ? ' scale(1.13)' : ''};z-index:${hot ? 9 : 7};display:flex;flex-direction:column;align-items:center;cursor:${cursor};transition:transform .12s;filter:${filter};${dim}`;
  }
}

function updateInfo() {
  const st = getState();
  let totalBet = 0, myTotal = 0;
  for (const slot of slots()) {
    totalBet += Number(st.betsBySlot[String(slot.id)] || 0);
    myTotal += Number(st.myBets[String(slot.id)] || 0);
  }
  dom.totalBet.textContent = fmtK(totalBet);
  dom.myTotalBet.textContent = fmtK(myTotal);
  dom.coins.textContent = st.balance == null ? '—' : fmtComma(st.balance);
  if (st.round && st.round.roundNumber != null) dom.round.textContent = st.round.roundNumber;
  dom.winToday.textContent = fmtK(st.sessionWin || 0);
}

function updateCenterBig() {
  const phase = getState().status;
  let txt = '…';
  if (phase === 'betting') {
    const secs = Math.ceil(authoritativeRemainingMs() / 1000);
    txt = Math.max(0, secs) + 's';
  } else if (phase === 'spinning') {
    txt = 'GO!';
  } else if (phase === 'result') {
    txt = '★';
  }
  if (dom.centerBig.textContent !== txt) dom.centerBig.textContent = txt;
}

function updateLights() {
  const fast = getState().status === 'spinning';
  for (let i = 0; i < dom.lightNodes.length; i++) {
    const lit = ((i + ui.frame * (fast ? 2 : 1)) % 6) < 3;
    const el = dom.lightNodes[i];
    el.style.background = lit
      ? 'radial-gradient(circle at 40% 35%,#fffdf0,#ffe14d 65%,#ffb300)'
      : 'radial-gradient(circle at 40% 35%,#d9591f,#8a2c0c)';
    el.style.boxShadow = lit ? '0 0 9px 2px rgba(255,225,90,.95),0 0 3px rgba(255,255,255,.8)' : 'none';
  }
}

function showResult(res) {
  ui.result = res;
  dom.resultEmoji.textContent = res.win ? '🎉' : (res.emoji || '');
  dom.resultTitle.textContent = res.win ? 'YOU WIN!' : 'RESULT';
  dom.resultTitle.style.color = res.win ? '#1f9d4d' : '#c0331f';
  // `pending` means the server's payout has not arrived, so `res.amount` is a
  // local estimate. Say so rather than presenting an unconfirmed figure as a
  // credited win — that mismatch is what players reported.
  if (res.cancelled) {
    dom.resultTitle.textContent = 'ROUND CANCELLED';
    dom.resultTitle.style.color = '#c0331f';
  }
  dom.resultSub.textContent = res.cancelled
    ? (res.refunded ? 'Your bets have been refunded.' : 'Any diamonds owed will be credited.')
    : res.pending
      ? `${res.emoji}  ×${res.mult}  →  confirming payout…`
      : res.win
        ? `${res.emoji}  ×${res.mult}  →  +${fmtComma(res.amount)}`
        : `${res.emoji}  ×${res.mult}  wins`;

  // This user's total bet for the round + their net win/loss.
  const totalBet = Number(res.totalBet || 0);
  const net = Number(res.net || 0);
  if (totalBet > 0) {
    dom.resultBet.textContent = `Total bet: ${fmtComma(totalBet)}`;
    dom.resultBet.style.display = '';
    dom.resultNet.textContent = net >= 0 ? `+${fmtComma(net)}` : `−${fmtComma(-net)}`;
    dom.resultNet.style.color = net >= 0 ? '#1f9d4d' : '#c0331f';
    dom.resultNet.style.display = '';
  } else {
    dom.resultBet.style.display = 'none';
    dom.resultNet.style.display = 'none';
  }

  // Top three winners of the round (only when someone actually won).
  const winners = Array.isArray(res.winners) ? res.winners : [];
  if (winners.length) {
    const rows = winners.slice(0, 3).map((w, i) => {
      const name = String(w.userName || 'Player').slice(0, 12);
      const medal = ['🥇', '🥈', '🥉'][i] || `${i + 1}`;
      return `<div style="display:flex;align-items:center;gap:7px;padding:4px 7px;border-radius:9px;background:${i % 2 ? 'rgba(122,31,18,.06)' : 'rgba(255,174,0,.14)'};margin-bottom:4px;font-family:'Baloo 2';font-size:12px;">
        <span style="flex:none;width:18px;text-align:center;">${medal}</span>
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:700;">${name}</span>
        <span style="flex:none;font-family:'Luckiest Guy',cursive;color:#1f9d4d;">+${fmtComma(w.payout)}</span>
      </div>`;
    }).join('');
    dom.resultWinners.innerHTML = `<div style="font-family:'Baloo 2';font-weight:800;font-size:12px;color:#7a3b00;text-align:center;margin-bottom:5px;">🏆 Top Winners</div>${rows}`;
    dom.resultWinners.style.display = '';
  } else {
    dom.resultWinners.innerHTML = '';
    dom.resultWinners.style.display = 'none';
  }

  dom.result.style.display = '';
  dom.result.style.animation = 'none';
  // force reflow to restart the pop animation
  void dom.result.offsetWidth;
  dom.result.style.animation = 'mw-pop .4s ease-out';
}

function hideResult() {
  ui.result = null;
  dom.result.style.display = 'none';
}

function hideLoading() {
  if (!ui.loading) return;
  ui.loading = false;
  if (dom.loading) dom.loading.style.display = 'none';
}

function renderAll() {
  updateBaskets();
  updateInfo();
  updateRecent();
  updateChips();
  updateAuto();
  updateCenterBig();
}

// ===================================================================
// Bet placement (optimistic + batched, ported from Ferris).
// ===================================================================
function placeBet(slotId, pos) {
  const st = getState();
  if (st.status !== 'betting' || !st.round) return;
  const slot = slots().find((s) => Number(s.id) === Number(slotId));
  if (!slot || slot.bettable === false) return; // non-bettable slots can't be bet on
  const amount = ui.selectedChip;
  if (!amount || amount <= 0) return;
  if (st.balance != null && st.balance < amount) return;
  const cfg = st.config || {};
  if (cfg.minBetPerSlot && amount < cfg.minBetPerSlot) return;

  const key = String(slotId);
  // Cap distinct slots per round (server enforces the same via `too_many_spots`).
  const maxSpots = cfg.maxSpotsPerUser || 6;
  const alreadyBet = Number(st.myBets[key]) > 0;
  if (!alreadyBet && Object.keys(st.myBets).length >= maxSpots) return;

  const myBets = { ...st.myBets, [key]: (Number(st.myBets[key]) || 0) + amount };
  const betsBySlot = { ...st.betsBySlot, [key]: (Number(st.betsBySlot[key]) || 0) + amount };
  const balance = st.balance == null ? null : st.balance - amount;
  setState({ myBets, betsBySlot, balance });
  haptic('light');
  enqueueBetTap({ roundId: st.round._id, slotId, amount, clientNonce: makeNonce() });
}

function enqueueBetTap(tap) {
  betQueue.push(tap);
  const remaining = authoritativeRemainingMs() || 0;
  scheduleBetFlush(remaining > 0 && remaining <= BET_BATCH_FINAL_FLUSH_MS ? 0 : BET_BATCH_DELAY_MS);
}

function scheduleBetFlush(delay) {
  if (betFlushTimer) clearTimeout(betFlushTimer);
  betFlushTimer = setTimeout(flushBetQueue, delay);
}

// Taps sent but not yet acknowledged; with `betQueue` the only stake the client
// may add on top of the server's figure (see mergeMyBets).
let inFlightTaps = [];
// A /round/current response requested before the last acknowledged batch may
// predate it, so its myBets is not applied.
let lastBetAckAt = 0;
const BET_RETRY_ATTEMPTS = 3;

async function flushBetQueue() {
  if (betFlushInFlight || betQueue.length === 0) return;
  const roundId = betQueue[0].roundId;
  // Only flush taps belonging to the current round.
  const sameRound = betQueue.filter((t) => t.roundId === roundId);
  const batch = sameRound.slice(0, BET_BATCH_MAX_TAPS);
  betQueue = betQueue.filter((t) => !(t.roundId === roundId && batch.includes(t)));

  betFlushInFlight = true;
  inFlightTaps = batch;
  // One nonce for every attempt: the server applies a nonce once, so retrying
  // after a lost response can never charge twice.
  const batchNonce = `${Date.now()}-${betBatchSeq++}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    let res = null;
    let lastErr = null;
    for (let attempt = 1; attempt <= BET_RETRY_ATTEMPTS; attempt++) {
      try {
        res = await api.placeBetBatch({
          roundId,
          taps: batch.map(({ slotId, amount, clientNonce }) => ({ slotId, amount, clientNonce })),
          batchNonce,
          clientRttHintMs: lastRttMs,
          clientRemainingMs: authoritativeRemainingMs(),
        });
        lastErr = null;
        break;
      } catch (e) {
        lastErr = e;
        if (classifyBetFailure(e) !== 'retry' || attempt === BET_RETRY_ATTEMPTS || destroyed) break;
        await new Promise((r) => setTimeout(r, 300 * attempt));
      }
    }
    if (lastErr) throw lastErr;
    lastBetAckAt = Date.now();
    if (res && res.userId) ui.myUserId = res.userId;
    if (res && res.balance != null) setState({ balance: res.balance });
    const rejected = (res && res.rejectedTaps) || [];
    if (rejected.length > 0) rollbackTaps(rejected);
  } catch (e) {
    const verdict = classifyBetFailure(e);
    inFlightTaps = [];
    rollbackTaps(batch);
    try { api.diag({ ts: Date.now(), event: 'bet_batch_failed', verdict, tapCount: batch.length, error: e && e.message }); } catch (_) {}
    if (verdict === 'reject') {
      showToast(prettyError(e && e.message), 'error');
    } else {
      // Unknown outcome: the server may have accepted it. Take its word for
      // stake and balance rather than guessing either way.
      lastBetAckAt = Date.now();
      showToast('Connection problem — checking your bet…', 'error');
      refreshCurrentRound({ forceMyBets: true });
      refreshBalance();
    }
  } finally {
    inFlightTaps = [];
    betFlushInFlight = false;
    if (betQueue.length > 0) scheduleBetFlush(0);
  }
}

/**
 * Re-read the live round, including the server's record of MY stake on it —
 * what lets a player who reopened the game mid-round see their bets and their
 * result.
 */
async function refreshCurrentRound({ forceMyBets = false } = {}) {
  const requestedAt = Date.now();
  try {
    const r = await api.getCurrentRound();
    if (!r) return;
    if (r.userId) ui.myUserId = r.userId;
    if (r.balance != null) setState({ balance: r.balance });
    applyRound(r.round, { remainingMs: r.remainingMs, serverNow: r.serverNow, rttMs: r._rttMs, source: 'http' });
    if (r.round && r.myBets && (forceMyBets || lastBetAckAt < requestedAt)) {
      setState({ myBets: mergeMyBets(r.myBets, [...inFlightTaps, ...betQueue], r.round._id) });
    }
  } catch (_) {}
}

/** The caller's authoritative result for a round, retried until settled. */
async function loadMyResult(roundId, { attempts = 4 } = {}) {
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await api.getMyResult(roundId);
      if (res && res.balance != null) setState({ balance: res.balance });
      const result = res && res.result;
      if (result && (result.settled || result.voided || result.totalBet === 0)) return result;
    } catch (_) {}
    await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
  }
  return null;
}

function rollbackTaps(taps) {
  const st = getState();
  const myBets = { ...st.myBets };
  const betsBySlot = { ...st.betsBySlot };
  let refund = 0;
  for (const t of taps) {
    const key = String(t.slotId);
    const amt = Number(t.amount) || 0;
    myBets[key] = Math.max(0, (Number(myBets[key]) || 0) - amt);
    betsBySlot[key] = Math.max(0, (Number(betsBySlot[key]) || 0) - amt);
    refund += amt;
  }
  const balance = st.balance == null ? null : st.balance + refund;
  setState({ myBets, betsBySlot, balance });
}

// Lightweight toast (ported from Ferris `hud.js`). Monkey Wheel has no other
// user-facing message surface, so this is used to explain rejected bets.
function showToast(msg, kind = 'success', durationMs = 2200) {
  const t = document.createElement('div');
  t.className = `toast ${kind}`;
  t.setAttribute('role', kind === 'error' ? 'alert' : 'status');
  t.setAttribute('aria-live', kind === 'error' ? 'assertive' : 'polite');
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => { t.style.opacity = '0'; t.style.transition = 'opacity 0.25s'; }, durationMs - 250);
  setTimeout(() => { t.remove(); }, durationMs);
}

// Maps server bet-rejection codes to human messages (mirrors Ferris).
function prettyError(code) {
  switch (code) {
    case 'insufficient_diamonds': return 'Not enough diamonds';
    case 'too_many_spots': return 'Too many slots picked';
    case 'betting_closed': return 'Betting closed';
    case 'feature_disabled': return 'Game disabled';
    case 'slot_max_exceeded': return 'Slot bet limit reached';
    case 'amount_below_min': return 'Bet too small';
    case 'invalid_slot': return 'Invalid slot';
    case 'no_active_round': return 'No active round';
    case 'user_id_locked': return 'ID locked accounts can watch only';
    case 'id_locked': return 'ID locked accounts can watch only';
    default: return code || 'Bet failed';
  }
}

// ===================================================================
// Spin animation (server-driven; lands on winningSlotId near spinEndsAt).
// ===================================================================
function startSpinAnimation(winningSlotId, spinEndsAt) {
  if (spinTimer) { clearTimeout(spinTimer); spinTimer = null; }
  const slot = slotById(winningSlotId);
  const winnerPos = slot ? Number(slot.position) : 0;
  const targetIdx = Math.max(0, LOOP.indexOf(winnerPos));
  const len = LOOP.length;
  const loops = 3;
  const totalSteps = loops * len + targetIdx;
  let k = 0;
  const step = () => {
    if (destroyed) return;
    ui.spinPos = LOOP[k % len];
    if (k >= totalSteps) {
      ui.spinPos = winnerPos;
      return;
    }
    const prog = k / totalSteps;
    const delay = 42 + 360 * Math.pow(prog, 2.6);
    k += 1;
    spinTimer = setTimeout(step, delay);
  };
  step();
}

// ===================================================================
// Apply a server round view to the store.
// ===================================================================
function applyRound(round, { remainingMs, serverNow: sNow, rttMs, source } = {}) {
  if (sNow) applyServerNow(sNow, rttMs);
  if (typeof remainingMs === 'number') applyServerRemaining(remainingMs, source);
  if (!round) { setState({ status: 'idle' }); return; }
  const patch = {
    round,
    // 'closed' is the instant between the last bet and the reveal: locked.
    status: round.status === 'closed' ? 'spinning' : round.status,
    winningSlotId: round.winningSlotId != null ? round.winningSlotId : null,
  };
  if (round.betsBySlot && typeof round.betsBySlot === 'object') patch.betsBySlot = { ...round.betsBySlot };
  setState(patch);
}

// ===================================================================
// Socket event wiring.
// ===================================================================
function wireSocket() {
  on('monkeywheel_state', (p) => {
    if (!p) return;
    applyRound(p.round, { remainingMs: p.remainingMs, serverNow: p.serverNow, source: 'ws_snapshot' });
    if (Array.isArray(p.activity)) setState({ activity: p.activity });
    hideLoading();
  });

  on('monkeywheel_round_started', (p) => {
    if (!p || !p.round) return;
    // New round — reset my bets + result; replay auto-bets.
    hideResult();
    ui.spinPos = -1;
    setState({ myBets: {}, betsBySlot: p.round.betsBySlot || {}, winningSlotId: null });
    applyRound(p.round, { remainingMs: p.remainingMs, serverNow: p.serverNow, source: 'ws_round_event' });
    maybeAutoBet();
  });

  on('monkeywheel_bet_placed', (p) => {
    if (!p || !p.betsBySlot) return;
    if (p.serverNow) applyServerNow(p.serverNow, null);
    const st = getState();
    if (!st.round || st.round._id !== p.roundId) return;
    setState({ betsBySlot: { ...p.betsBySlot } });
  });

  on('monkeywheel_round_spinning', (p) => {
    if (!p) return;
    if (p.serverNow) applyServerNow(p.serverNow, null);
    if (typeof p.remainingMs === 'number') applyServerRemaining(p.remainingMs, 'ws_round_event');
    // snapshot my bets for auto-replay before the round resets
    ui.lastBets = { ...getState().myBets };
    setState({ status: 'spinning', winningSlotId: p.winningSlotId });
    haptic('medium');
    audio.spin();
    const spinEndsAt = p.spinEndsAt ? new Date(p.spinEndsAt).getTime() : Date.now() + 5000;
    startSpinAnimation(p.winningSlotId, spinEndsAt);
  });

  on('monkeywheel_round_finished', (p) => {
    if (!p || !p.round) return;
    if (p.serverNow) applyServerNow(p.serverNow, null);
    const round = p.round;
    // A round other than the one on screen (a late settlement the sweeper
    // recovered) must not flip the live round into 'result' and block betting.
    const cur = getState().round;
    if (p.recovered || !isCurrentRound(round._id, cur && cur._id)) {
      loadMyResult(round._id, { attempts: 1 }).then((res) => {
        if (res && res.totalBet > 0) {
          showToast(res.totalPayout > 0 ? `Round #${round.roundNumber} paid out ${fmtComma(res.totalPayout)}` : `Round #${round.roundNumber} settled`, 'success');
        }
      });
      requestBalanceRefresh();
      refreshBalance();
      return;
    }
    ui.spinPos = (slotById(round.winningSlotId) || {}).position ?? -1;
    setState({
      status: 'result',
      round,
      winningSlotId: round.winningSlotId,
      lastFinishedRound: round,
      betsBySlot: round.betsBySlot ? { ...round.betsBySlot } : getState().betsBySlot,
    });
    if (Array.isArray(p.activity)) setState({ activity: p.activity });
    resolveMyResult(round, p.winners);
    // Then replace the player's figures with the server's per-bet record.
    loadMyResult(round._id).then((res) => {
      if (!res || !ui.result) return;
      const st = getState();
      if (!isCurrentRound(round._id, st.round && st.round._id)) return;
      const sum = summarizeMyResult(res);
      if (sum.kind === 'none') return;
      showResult({
        ...ui.result,
        win: sum.totalPayout > 0,
        amount: sum.totalPayout,
        totalBet: sum.totalBet,
        net: sum.net,
        pending: sum.kind === 'pending' || sum.kind === 'review',
      });
    });
    pushRecent(round.winningSlotId);
    requestBalanceRefresh();
    refreshBalance();
  });

  // Settlement was retried and abandoned; the round was refunded server-side.
  // Without this the result popup keeps showing a win that will never be paid —
  // the winning slot reaches the client ~5s before settlement even runs.
  // Settlement of the current round is being retried; the outcome stands.
  on('monkeywheel_round_settle_pending', (p) => {
    const cur = getState().round;
    if (!isCurrentRound(p && p.roundId, cur && cur._id)) return;
    if (ui.result && ui.result.win) showResult({ ...ui.result, pending: true });
  });

  // Legacy servers refunded a round whose settlement failed.
  on('monkeywheel_round_settle_failed', (p) => {
    const cur = getState().round;
    if (!isCurrentRound(p && p.roundId, cur && cur._id)) return;
    const refunded = !!(p && p.refunded);
    showResult({
      win: false,
      amount: 0,
      emoji: '⚠️',
      mult: 0,
      totalBet: 0,
      net: 0,
      cancelled: true,
      refunded,
      winners: [],
    });
    showToast(
      refunded ? 'Round cancelled — your bets were refunded' : 'Round could not be completed',
      'error'
    );
    requestBalanceRefresh();
    refreshBalance();
  });

  on('monkeywheel_round_snapshot', (p) => {
    if (!p || !p.round) return;
    // Don't clobber an in-progress spin animation with a snapshot.
    if (getState().status === 'spinning' && p.round.status === 'spinning') {
      if (p.serverNow) applyServerNow(p.serverNow, null);
      return;
    }
    applyRound(p.round, { remainingMs: p.remainingMs, serverNow: p.serverNow, source: 'ws_snapshot' });
    if (Array.isArray(p.activity)) setState({ activity: p.activity });
  });

  on('monkeywheel_time_sync', (p) => {
    if (!p) return;
    if (p.serverNow) applyServerNow(p.serverNow, null);
    if (typeof p.remainingMs === 'number') applyServerRemaining(p.remainingMs, 'ws_time_sync');
    if (p.status && getState().status !== 'spinning') setState({ status: p.status });
  });

  on('monkeywheel_activity', (p) => {
    if (p && Array.isArray(p.activity)) setState({ activity: p.activity });
  });

  on('monkeywheel_pong', (p) => {
    if (!p || !p.clientSentAt) return;
    const rtt = Date.now() - p.clientSentAt;
    lastRttMs = rtt;
    applyServerNow(p.serverNow, rtt);
    if (dom.ping) dom.ping.innerHTML = `📶 ${Math.round(rtt)}ms`;
  });
}

// Collapse the per-bet winners array from the server into per-user totals,
// sorted by payout descending. A user with several winning bets is summed once.
function aggregateWinners(rawWinners) {
  if (!Array.isArray(rawWinners) || !rawWinners.length) return [];
  const byUser = new Map();
  for (const w of rawWinners) {
    const payout = Math.floor(Number(w.payout || 0));
    if (payout <= 0) continue;
    const key = String(w.customUserId || w.userId || w.userName || '');
    const cur = byUser.get(key);
    if (cur) cur.payout += payout;
    else byUser.set(key, { userName: w.userName || 'Player', payout });
  }
  return [...byUser.values()].sort((a, b) => b.payout - a.payout);
}

/**
 * Show this user's result for the round.
 *
 * ── The amount MUST come from the server ────────────────────────────────────
 *
 * This used to compute `Math.floor(myBet * mult)` in the browser and display
 * that as the amount won, using the server's `rawWinners` list only to build
 * the podium. So the figure the player saw had no connection to whether any
 * diamonds actually moved — and when settlement failed (which it did: one
 * unpayable winner rolled back the credit for the entire round) the game
 * cheerfully reported a win that was never paid.
 *
 * `rawWinners` is now the authority. Local math is a fallback used only when
 * the winners list has not arrived, and in that case the result is explicitly
 * marked pending rather than presented as a settled payout.
 *
 * Note a non-bettable bonus basket can now win. Nobody can bet on one, so
 * `myBet` is 0 and this correctly reports a loss for everyone.
 */
function resolveMyResult(round, rawWinners) {
  const winId = round.winningSlotId;
  const slot = slotById(winId);
  const mult = slot ? Number(slot.multiplier) : 0;
  const myBets = getState().myBets || {};
  const myBet = Number(myBets[String(winId)] || 0);
  const emoji = slot ? slot.emoji : '';

  // This user's total stake across every slot this round, and their net result.
  let totalBet = 0;
  for (const k of Object.keys(myBets)) totalBet += Number(myBets[k] || 0);

  // Set from the placeBetBatch response, so it is always known by the time
  // this user has anything staked.
  const currentUserId = ui.myUserId || null;
  const canAttribute = Array.isArray(rawWinners) && (currentUserId || myBet === 0);
  const serverPayout = canAttribute
    ? rawWinners
      .filter((w) => w && String(w.userId) === String(currentUserId))
      .reduce((sum, w) => sum + Math.max(0, Math.floor(Number(w.payout || 0))), 0)
    : null;

  // Server figure when we have it; local estimate only while it is in flight.
  const hasServerWinners = canAttribute;
  const pending = !hasServerWinners && myBet > 0;
  const payout = hasServerWinners ? serverPayout : Math.floor(myBet * mult);
  const net = payout - totalBet;
  const win = payout > 0;

  showResult({
    win,
    amount: payout,
    emoji,
    mult,
    totalBet,
    net,
    pending,
    winners: aggregateWinners(rawWinners),
  });
  if (win) audio.win(); else audio.lose();

  // Only bank a payout the server has confirmed, or the session total drifts
  // away from the real balance on every unsettled round.
  if (hasServerWinners && payout > 0) {
    const sessionWin = (getState().sessionWin || 0) + payout;
    setState({ sessionWin });
  }
}

function pushRecent(winningSlotId) {
  const slot = slotById(winningSlotId);
  if (!slot) return;
  const recent = [slot.emoji, ...(getState().recent || [])].slice(0, 8);
  setState({ recent });
}

function maybeAutoBet() {
  if (!ui.auto) return;
  const st = getState();
  if (!st.round || st.status !== 'betting') return;
  const last = ui.lastBets || {};
  for (const key of Object.keys(last)) {
    const amount = Number(last[key]) || 0;
    if (amount <= 0) continue;
    if (st.balance != null && st.balance < amount) break;
    // Re-stake the same per-slot total as last round, one tap each.
    placeBetRaw(Number(key), amount);
  }
}

function placeBetRaw(slotId, amount) {
  const st = getState();
  if (st.status !== 'betting' || !st.round) return;
  if (st.balance != null && st.balance < amount) return;
  const key = String(slotId);
  const myBets = { ...st.myBets, [key]: (Number(st.myBets[key]) || 0) + amount };
  const betsBySlot = { ...st.betsBySlot, [key]: (Number(st.betsBySlot[key]) || 0) + amount };
  const balance = st.balance == null ? null : st.balance - amount;
  setState({ myBets, betsBySlot, balance });
  enqueueBetTap({ roundId: st.round._id, slotId, amount, clientNonce: makeNonce() });
}

async function refreshBalance() {
  try {
    const r = await api.getBalance();
    if (r && r.balance != null) setState({ balance: r.balance });
  } catch (_) {}
}

// ===================================================================
// Input (event delegation on a stable root).
// ===================================================================
function wireInput() {
  document.getElementById('mw-app').addEventListener('click', (ev) => {
    const el = ev.target.closest('[data-action]');
    if (!el) return;
    const action = el.dataset.action;
    if (action === 'skip-load') { unlockAudio(); hideLoading(); return; }
    if (action === 'close') { sfx.tap(); closeGame(); return; }
    if (action === 'mute') { toggleMute(); return; }
    if (action === 'auto') { sfx.tap(); ui.auto = !ui.auto; updateAuto(); haptic('light'); return; }
    if (action === 'chip') {
      sfx.tap();
      ui.selectedChip = Number(el.dataset.amount) || ui.selectedChip;
      updateChips(); haptic('light'); return;
    }
    if (action === 'bet') {
      const slotId = Number(el.dataset.slotId);
      if (slotId) { sfx.bet(); placeBet(slotId, Number(el.dataset.pos)); }
      return;
    }
    // Info / settings panels.
    if (action === 'profile') { sfx.tap(); haptic('light'); openProfilePanel(); return; }
    if (action === 'moneyjar') { sfx.tap(); haptic('light'); openMoneyJarPanel(); return; }
    if (action === 'help') { sfx.tap(); haptic('light'); openHelpPanel(); return; }
    if (action === 'menu') { sfx.tap(); haptic('light'); openMenuPanel(); return; }
    if (action === 'menu-sound') { toggleMute(); openMenuPanel(); return; }   // re-render menu row
    if (action === 'menu-help') { sfx.tap(); openHelpPanel(); return; }
    if (action === 'menu-close') { sfx.tap(); closeGame(); return; }
    // Dismiss the modal when the backdrop or a close control is tapped (but not
    // when tapping inside the card, which has no close-modal action).
    if (action === 'close-modal') { sfx.tap(); closeModal(); return; }
    if (action === 'modal-card') { return; }   // clicks inside the card: keep open
  });
  // Unlock audio on the very first user gesture (covers any tap before the
  // tap-to-enter overlay is dismissed; browsers block autoplay until then).
  const firstGesture = () => { unlockAudio(); };
  window.addEventListener('pointerdown', firstGesture, { once: true });
}

function toggleMute() {
  ui.muted = !ui.muted;
  dom.sound.textContent = ui.muted ? '🔇' : '🔊';
  audio.setMuted(ui.muted);
}

// Reflect the persisted mute preference on the sound icon at startup.
function syncMuteIcon() {
  ui.muted = isAudioMuted();
  if (dom.sound) dom.sound.textContent = ui.muted ? '🔇' : '🔊';
}

// ===================================================================
// Info / settings panels (reuse one modal; content built from live state).
// ===================================================================
function openModal(icon, title, bodyHtml) {
  dom.modalIcon.textContent = icon || '';
  dom.modalTitle.textContent = title || '';
  dom.modalBody.innerHTML = bodyHtml || '';
  dom.modalBody.scrollTop = 0;
  dom.modal.style.display = 'flex';
}
function closeModal() {
  if (dom.modal) dom.modal.style.display = 'none';
}

function statRow(label, value, valueColor = '#7a1f12') {
  return `<div style="display:flex;justify-content:space-between;align-items:center;background:rgba(122,31,18,.08);border:1.5px solid rgba(255,174,0,.5);border-radius:10px;padding:8px 11px;margin-bottom:8px;">
    <span style="font-weight:700;">${label}</span>
    <span style="font-family:'Luckiest Guy',cursive;font-size:16px;color:${valueColor};">${value}</span>
  </div>`;
}

function openProfilePanel() {
  const st = getState();
  const round = st.round && st.round.roundNumber != null ? st.round.roundNumber : '—';
  const body = `
    <div style="display:flex;flex-direction:column;align-items:center;margin-bottom:12px;">
      <div style="width:64px;height:64px;border-radius:50%;background:linear-gradient(#ffb43f,#e07c12);border:3px solid #fff3cf;display:flex;align-items:center;justify-content:center;font-size:34px;box-shadow:0 3px 7px rgba(0,0,0,.4);">👤</div>
      <div style="font-family:'Luckiest Guy',cursive;font-size:18px;color:#7a1f12;margin-top:6px;">Player</div>
    </div>
    ${statRow('🪙 Game Coins', st.balance == null ? '—' : fmtComma(st.balance), '#c87b00')}
    ${statRow('🏆 Won today', fmtComma(st.sessionWin || 0), '#1f9d4d')}
    ${statRow('🎯 Current round', round)}
  `;
  openModal('👤', 'My Profile', body);
}

function openMoneyJarPanel() {
  const st = getState();
  const activity = Array.isArray(st.activity) ? st.activity : [];
  let list;
  if (!activity.length) {
    list = `<div style="text-align:center;opacity:.7;padding:18px 0;">No winners yet in the last 24h.<br>Place a bet to get on the board! 🐵</div>`;
  } else {
    list = activity.slice(0, 15).map((a, i) => {
      const net = Math.round(Number(a.netAmount || 0));
      const netColor = net >= 0 ? '#1f9d4d' : '#c0331f';
      const netStr = (net >= 0 ? '+' : '') + fmtComma(net);
      const name = (a.userName || 'Player').toString().slice(0, 14);
      return `<div style="display:flex;align-items:center;gap:8px;padding:7px 9px;border-radius:10px;background:${i % 2 ? 'rgba(122,31,18,.06)' : 'rgba(255,174,0,.12)'};margin-bottom:5px;">
        <span style="flex:none;width:18px;text-align:center;font-weight:800;color:#c87b00;">${i + 1}</span>
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:700;">${name}</span>
        <span style="flex:none;font-family:'Luckiest Guy',cursive;color:${netColor};">${netStr}</span>
      </div>`;
    }).join('');
  }
  const body = `
    ${statRow('🪙 Game Coins', st.balance == null ? '—' : fmtComma(st.balance), '#c87b00')}
    ${statRow('🏆 Won today', fmtComma(st.sessionWin || 0), '#1f9d4d')}
    <div style="font-family:'Luckiest Guy',cursive;font-size:15px;color:#7a1f12;margin:10px 0 7px;">Top players (24h)</div>
    ${list}
  `;
  openModal('🐷', 'Money Jar', body);
}

function openHelpPanel() {
  const rows = slots()
    .slice()
    .sort((a, b) => Number(b.multiplier || 0) - Number(a.multiplier || 0))
    .map((s) => `<div style="display:flex;align-items:center;gap:8px;padding:6px 9px;border-radius:9px;background:rgba(255,174,0,.12);margin-bottom:5px;">
      <span style="font-size:20px;flex:none;">${s.emoji || '🎁'}</span>
      <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:700;">${s.label || ('Slot ' + s.id)}</span>
      ${s.probability ? `<span style="flex:none;font-size:11px;opacity:.7;">${(s.probability * 100).toFixed(1)}%</span>` : ''}
      <span style="flex:none;font-family:'Luckiest Guy',cursive;color:#c0331f;">${s.bettable === false ? 'house' : `×${s.multiplier}`}</span>
    </div>`).join('');
  const rtp = getState().config && getState().config.rtp;
  const body = `
    <p style="margin:0 0 10px;line-height:1.45;">Bet on which basket the wheel lands on. If your basket wins, you’re paid your bet × that basket’s multiplier!</p>
    <div style="font-family:'Luckiest Guy',cursive;font-size:15px;color:#7a1f12;margin:4px 0 7px;">How to play</div>
    <ol style="margin:0 0 12px;padding-left:18px;line-height:1.6;">
      <li><b>Select</b> a coin amount from the chips.</li>
      <li><b>Tap a basket</b> to place your bet (tap again to add more).</li>
      <li>When the timer hits 0 the wheel <b>spins</b>.</li>
      <li>Win coins if it lands on a basket you bet on. 🎉</li>
    </ol>
    <p style="margin:0 0 12px;opacity:.8;">Tip: <b>auto bet</b> repeats your last round’s bets automatically.</p>
    <div style="font-family:'Luckiest Guy',cursive;font-size:15px;color:#7a1f12;margin:4px 0 7px;">Payout multipliers</div>
    ${rows || '<div style="opacity:.7;">Loading…</div>'}
    <p style="margin:10px 0 0;font-size:12px;opacity:.8;line-height:1.4;">The % is how often each basket wins. Baskets marked <b>house</b> cannot be bet on; when one wins, every bet loses.${rtp ? ` Every bettable basket pays back <b>${Math.round(rtp * 1000) / 10}%</b> of what is bet on it on average over many rounds (RTP) — not the chance of winning.` : ''} Odds are fixed and never depend on who bet what.</p>
  `;
  openModal('❓', 'How to Play', body);
}

function openMenuPanel() {
  const muteLabel = ui.muted ? '🔇 Sound: Off' : '🔊 Sound: On';
  const btn = (action, label, bg) => `<div data-action="${action}" style="text-align:center;padding:11px;border-radius:12px;font-weight:800;font-size:15px;color:#fff;cursor:pointer;margin-bottom:10px;background:${bg};box-shadow:0 3px 6px rgba(0,0,0,.3);">${label}</div>`;
  const body = `
    ${btn('menu-sound', muteLabel, 'linear-gradient(#3f7d35,#2a5a23)')}
    ${btn('menu-help', '❓ How to Play', 'linear-gradient(#ffb43f,#e07c12)')}
    ${btn('menu-close', '🚪 Exit Game', 'linear-gradient(#ff5a6e,#d62b46)')}
  `;
  openModal('☰', 'Menu', body);
}

// ===================================================================
// Audio — synthesized via Web Audio (src/sound.js). No asset files or backend
// theme URLs required; background music + SFX always play once the audio
// context is unlocked by the first user gesture (tap-to-enter / any tap).
// ===================================================================
const audio = {
  start() { unlockAudio(); },                 // primes ctx + starts ambient music
  setMuted(m) { setAudioMuted(m); },
  spin() { sfx.spin(); },
  win() { sfx.win(); },
  lose() { sfx.lose(); },
};

// ===================================================================
// Animation frame loop (lights, countdown, spin glow).
// ===================================================================
function startFrameLoop() {
  frameTimer = setInterval(() => {
    if (destroyed) return;
    ui.frame += 1;
    updateLights();
    updateCenterBig();
    if (getState().status === 'spinning' || getState().status === 'result') updateBaskets();
  }, 110);
}

// ===================================================================
// Bootstrap.
// ===================================================================
async function bootstrap() {
  buildStage();
  wireInput();
  syncMuteIcon();
  startLoadingProgress();

  // Load config first (needed to build baskets/chips).
  try {
    const cfgRes = await api.getConfig();
    if (cfgRes && cfgRes.config) setState({ config: cfgRes.config });
  } catch (e) { /* keep loading screen; will retry via socket snapshot */ }

  buildBaskets();
  buildChips();
  renderAll();

  // Initial round, balance and MY stake on the live round.
  await refreshCurrentRound({ forceMyBets: true });
  onSocketLifecycle('connect', () => { refreshCurrentRound(); refreshBalance(); });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && !destroyed) { refreshCurrentRound(); refreshBalance(); }
  });
  try {
    const a = await api.getActivity(20);
    if (a && Array.isArray(a.activity)) {
      setState({ activity: a.activity });
      // Seed recent strip from finished-round activity winning slots if present.
    }
  } catch (_) {}

  wireSocket();
  await connectSocket();
  startPing();
  startFrameLoop();

  // Re-render whenever store changes.
  subscribe(() => { renderAll(); });
  renderAll();

  // Finish loading once we have config.
  if (getState().config) markLoadComplete();
}

function startLoadingProgress() {
  ui.loadProgress = 0;
  loadTimer = setInterval(() => {
    ui.loadProgress = Math.min(ui.loadComplete ? 100 : 90, ui.loadProgress + 7 + Math.random() * 7);
    const p = Math.round(ui.loadProgress);
    if (dom.loadBar) dom.loadBar.style.width = p + '%';
    if (dom.loadPct) dom.loadPct.textContent = p;
    if (p >= 100) { clearInterval(loadTimer); loadTimer = null; setTimeout(hideLoading, 250); audio.start(); }
  }, 60);
}

function markLoadComplete() { ui.loadComplete = true; }

function startPing() {
  const tick = () => { if (!destroyed) emit('monkeywheel_ping', { clientSentAt: Date.now() }); };
  tick();
  pingTimer = setInterval(tick, 5000);
}

// ===================================================================
// Cleanup (called by Flutter via window.__MONKEYWHEEL_CLEANUP on close).
// ===================================================================
function cleanup() {
  destroyed = true;
  try { disconnectSocket(); } catch (_) {}
  [betFlushTimer, spinTimer, resultHideTimer, loadTimer, frameTimer, pingTimer].forEach((t) => { if (t) clearTimeout(t); });
  if (frameTimer) clearInterval(frameTimer);
  if (pingTimer) clearInterval(pingTimer);
  if (loadTimer) clearInterval(loadTimer);
  try { audio.setMuted(true); } catch (_) {}
}
window.__MONKEYWHEEL_CLEANUP = cleanup;

bootstrap();
